/**
 * Componente de resumo gerado por IA
 * Com suporte a regeneração e customização para advogados criminalistas
 */

import React, { useState, useEffect, useRef } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import RefreshIcon from '@mui/icons-material/Refresh'
import TuneIcon from '@mui/icons-material/Tune'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import GavelIcon from '@mui/icons-material/Gavel'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TopicIcon from '@mui/icons-material/Topic'
import DescriptionIcon from '@mui/icons-material/Description'
import PeopleIcon from '@mui/icons-material/People'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AISummary } from '../../../types/transcription'
import { getAISummary, regenerateAISummary, runOnDemandAnalysis, SummaryCustomization } from '../../../services/api'
import AnalysisOutdatedBanner from './AnalysisOutdatedBanner'
import { contextualErrors, FriendlyError } from '../../../services/errorHandler'
import {
  BETA_LIMITS,
  canRegenerateSummary,
  incrementSummaryRegenCount,
  getSummaryRegenCount
} from '../../../utils/betaLimits'
import { useBetaMode } from '../../../contexts'
import { SpeakerMapping } from '../../../services/exportService'
import AnalysisSkippedCard from './AnalysisSkippedCard'
import AnalysisProcessingCard from './AnalysisProcessingCard'

type Props = {
  fileId: string
  /** Status da análise de resumo: PENDING, PROCESSING, COMPLETED, FAILED, SKIPPED */
  summaryStatus?: string
  /** Mapeamento de nomes personalizados para speakers */
  speakerMapping?: SpeakerMapping
  onSummaryGenerated?: (summary: AISummary) => void
  /** Callback quando análise on-demand é iniciada */
  onAnalysisStarted?: () => void
  /** Flag indicando que segmentos foram editados após esta análise */
  analysisOutdated?: boolean
}

// Helper para formatar duração em segundos
function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`
  } else {
    return `${secs}s`
  }
}

/**
 * Monta o texto do resumo formatado para copiar
 */
function buildSummaryPlainText(s: AISummary, mapping: SpeakerMapping = {}): string {
  const mapName = (name: string) => mapping[name] || name
  const lines: string[] = []
  lines.push('RESUMO COM IA')
  lines.push('='.repeat(40))
  lines.push('')
  if (s.overview) { lines.push(s.overview); lines.push('') }
  if (s.mainTopic) lines.push(`Tópico Principal: ${s.mainTopic}`)
  if (s.context)   lines.push(`Contexto: ${s.context}`)
  if (s.participants?.length) lines.push(`Participantes: ${s.participants.map(mapName).join(', ')}`)
  if (s.duration)  lines.push(`Duração: ${formatDuration(s.duration)}`)
  lines.push('')
  if (s.keyPoints?.length) {
    lines.push('PONTOS-CHAVE')
    lines.push('-'.repeat(30))
    s.keyPoints.forEach((p, i) => lines.push(`  ${i + 1}. ${p}`))
    lines.push('')
  }
  if (s.actionItems?.length) {
    lines.push('AÇÕES PENDENTES')
    lines.push('-'.repeat(30))
    s.actionItems.forEach((a, i) => lines.push(`  ${i + 1}. ${a}`))
    lines.push('')
  }
  if (s.decisions?.length) {
    lines.push('DECISÕES')
    lines.push('-'.repeat(30))
    s.decisions.forEach((d, i) => lines.push(`  ${i + 1}. ${d}`))
    lines.push('')
  }
  return lines.join('\n')
}

/**
 * Gera HTML formatado para impressão/PDF
 */
function buildSummaryHTML(s: AISummary, mapping: SpeakerMapping = {}): string {
  const mapName = (name: string) => mapping[name] || name
  const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')
  let html = '<!DOCTYPE html><html><head><meta charset="utf-8"/>'
  html += '<title>Resumo com IA</title>'
  html += '<style>'
  html += '@page { margin: 2cm; }'
  html += 'body { font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; color: #222; line-height: 1.6; max-width: 700px; margin: 0 auto; padding: 20px; }'
  html += 'h1 { font-size: 20px; color: #1565c0; border-bottom: 2px solid #1565c0; padding-bottom: 6px; }'
  html += 'h2 { font-size: 15px; color: #333; margin-top: 20px; margin-bottom: 6px; }'
  html += '.meta { color: #666; font-size: 13px; margin-bottom: 4px; }'
  html += '.meta b { color: #444; }'
  html += '.overview { background: #f5f5f5; padding: 12px 16px; border-radius: 6px; margin: 12px 0 16px; white-space: pre-line; }'
  html += 'ol { padding-left: 20px; }'
  html += 'li { margin-bottom: 4px; }'
  html += '.section-key { border-left: 4px solid #ed6c02; padding-left: 10px; }'
  html += '.section-action { border-left: 4px solid #2e7d32; padding-left: 10px; }'
  html += '.section-decision { border-left: 4px solid #1565c0; padding-left: 10px; }'
  html += '.footer { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 11px; color: #999; }'
  html += '</style></head><body>'
  html += '<h1>Resumo com IA</h1>'
  if (s.overview) html += `<div class="overview">${esc(s.overview)}</div>`
  if (s.mainTopic) html += `<div class="meta"><b>Tópico Principal:</b> ${esc(s.mainTopic)}</div>`
  if (s.context)   html += `<div class="meta"><b>Contexto:</b> ${esc(s.context)}</div>`
  if (s.participants?.length) html += `<div class="meta"><b>Participantes:</b> ${esc(s.participants.map(mapName).join(', '))}</div>`
  if (s.duration)  html += `<div class="meta"><b>Duração:</b> ${formatDuration(s.duration)}</div>`
  if (s.keyPoints?.length) {
    html += '<div class="section-key"><h2>Pontos-chave</h2><ol>'
    s.keyPoints.forEach(p => { html += `<li>${esc(p)}</li>` })
    html += '</ol></div>'
  }
  if (s.actionItems?.length) {
    html += '<div class="section-action"><h2>Ações Pendentes</h2><ol>'
    s.actionItems.forEach(a => { html += `<li>${esc(a)}</li>` })
    html += '</ol></div>'
  }
  if (s.decisions?.length) {
    html += '<div class="section-decision"><h2>Decisões</h2><ol>'
    s.decisions.forEach(d => { html += `<li>${esc(d)}</li>` })
    html += '</ol></div>'
  }
  const now = new Date().toLocaleString('pt-BR')
  html += `<div class="footer">Gerado automaticamente por IA em ${now}</div>`
  html += '</body></html>'
  return html
}

// ===============================================
// Opções de customização pré-definidas
// ===============================================

const FOCUS_OPTIONS = [
  { value: 'geral', label: 'Visão geral', description: 'Resumo equilibrado de todos os aspectos' },
  { value: 'fatos_evidencias', label: 'Fatos e evidências', description: 'Foco em provas, documentos e relatos de eventos' },
  { value: 'argumentacao', label: 'Argumentação jurídica', description: 'Teses, fundamentos legais e referências a leis' },
  { value: 'depoimentos', label: 'Depoimentos e testemunhos', description: 'O que cada pessoa declarou e versões conflitantes' },
  { value: 'cronologia', label: 'Cronologia dos fatos', description: 'Sequência temporal dos acontecimentos' },
]

const DETAIL_OPTIONS = [
  { value: 'resumido', label: 'Resumido', description: 'Apenas os pontos essenciais' },
  { value: 'moderado', label: 'Moderado', description: 'Equilíbrio entre síntese e detalhe' },
  { value: 'detalhado', label: 'Detalhado', description: 'Análise abrangente e completa' },
]

const PERSPECTIVE_OPTIONS = [
  { value: 'neutro', label: 'Neutro', description: 'Análise imparcial e objetiva' },
  { value: 'defesa', label: 'Perspectiva da defesa', description: 'Elementos favoráveis ao réu, nulidades e fragilidades da acusação' },
  { value: 'acusacao', label: 'Perspectiva da acusação', description: 'Materialidade, autoria, contradições e provas contundentes' },
]

const HIGHLIGHT_OPTIONS = [
  { value: 'contradicoes', label: 'Contradições', description: 'Entre depoimentos ou versões' },
  { value: 'provas', label: 'Provas e evidências', description: 'Documentos, gravações, perícias' },
  { value: 'datas', label: 'Datas e prazos', description: 'Prazos processuais e datas citadas' },
  { value: 'valores', label: 'Valores monetários', description: 'Quantias e valores mencionados' },
  { value: 'pessoas', label: 'Pessoas e relações', description: 'Nomes e vínculos entre envolvidos' },
  { value: 'emocao', label: 'Momentos de tensão', description: 'Trechos com carga emocional' },
  { value: 'admissoes', label: 'Admissões e confissões', description: 'Declarações auto-incriminatórias' },
  { value: 'direitos', label: 'Direitos fundamentais', description: 'Direitos mencionados ou violados' },
]

const FORMAT_OPTIONS = [
  { value: 'corrido', label: 'Texto corrido', description: 'Parágrafos contínuos' },
  { value: 'topicos', label: 'Tópicos organizados', description: 'Numerados por assunto' },
  { value: 'peticao', label: 'Linguagem de petição', description: 'Formal jurídico, para peças processuais' },
]

export default function AISummaryPanel({ fileId, summaryStatus, speakerMapping = {}, onSummaryGenerated, onAnalysisStarted, analysisOutdated }: Props) {
  // Helper para resolver nome personalizado do speaker
  const getSpeakerName = (speaker: string) => speakerMapping[speaker] || speaker
  const betaMode = useBetaMode()
  const [summary, setSummary] = useState<AISummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const autoLoadedRef = useRef(false)
  const [showCustomizationHint, setShowCustomizationHint] = useState(true)
  const [showDetails, setShowDetails] = useState(true)

  // Feedback visual
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  })

  // Auto-load: carrega o resumo automaticamente quando o status da análise indica que está pronto
  useEffect(() => {
    if (autoLoadedRef.current || summary || loading) return
    // Se o status é COMPLETED, busca automaticamente
    if (summaryStatus === 'COMPLETED') {
      autoLoadedRef.current = true
      handleGenerate()
    }
  }, [summaryStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // Se fileId mudar, reseta o auto-load
  useEffect(() => {
    autoLoadedRef.current = false
    setSummary(null)
  }, [fileId])

  const handleCopyToClipboard = async () => {
    if (!summary) return
    try {
      const text = buildSummaryPlainText(summary, speakerMapping)
      await navigator.clipboard.writeText(text)
      setSnackbar({ open: true, message: 'Resumo copiado para a área de transferência!', severity: 'success' })
    } catch {
      setSnackbar({ open: true, message: 'Não foi possível copiar. Tente novamente.', severity: 'error' })
    }
  }

  const handleDownloadPDF = () => {
    if (!summary) return
    try {
      const html = buildSummaryHTML(summary, speakerMapping)
      const printWindow = window.open('', '_blank', 'width=800,height=600')
      if (!printWindow) {
        setSnackbar({ open: true, message: 'Pop-up bloqueado pelo navegador. Permita pop-ups para baixar o PDF.', severity: 'error' })
        return
      }
      printWindow.document.write(html)
      printWindow.document.close()
      printWindow.onload = () => {
        printWindow.focus()
        printWindow.print()
      }
      // Fallback para navegadores que não disparam onload
      setTimeout(() => {
        try { printWindow.focus(); printWindow.print() } catch { /* já imprimiu */ }
      }, 500)
    } catch {
      setSnackbar({ open: true, message: 'Erro ao gerar PDF. Tente novamente.', severity: 'error' })
    }
  }

  // Customização
  const [showCustomization, setShowCustomization] = useState(false)
  const [regenCount, setRegenCount] = useState(() => getSummaryRegenCount(fileId))
  const [customization, setCustomization] = useState<SummaryCustomization>({
    focus: 'geral',
    detailLevel: 'moderado',
    perspective: 'neutro',
    highlights: [],
    outputFormat: 'corrido',
  })

  const handleGenerate = async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await getAISummary(fileId)
      if (result === null) {
        setError({
          type: 'not_found',
          title: 'Resumo não disponível',
          message: 'O resumo ainda não está disponível. O arquivo está sendo processado.',
          suggestion: 'Aguarde alguns minutos e tente novamente.',
          canRetry: true
        })
        return
      }
      setSummary(result)
      onSummaryGenerated?.(result)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerate = async () => {
    // BETA: limite de regenerações por arquivo
    if (betaMode && !canRegenerateSummary(fileId)) {
      setError({
        type: 'not_found',
        title: 'Limite de uso atingido',
        message: `Nesta versão de avaliação, o resumo pode ser regenerado no máximo ${BETA_LIMITS.maxSummaryRegenerations} ve${BETA_LIMITS.maxSummaryRegenerations !== 1 ? 'zes' : 'z'} por arquivo.`,
        suggestion: 'Entre em contato com o suporte para ter acesso completo, sem restrições de uso.',
        canRetry: false
      })
      return
    }

    setLoading(true)
    setError(null)
    setSummary(null)
    setShowCustomization(false)

    try {
      // Monta as opções — se tudo está no padrão, manda vazio (sem customização)
      const isDefault = customization.focus === 'geral'
        && customization.detailLevel === 'moderado'
        && customization.perspective === 'neutro'
        && (!customization.highlights || customization.highlights.length === 0)
        && customization.outputFormat === 'corrido'

      const opts = isDefault ? undefined : customization

      const result = await regenerateAISummary(fileId, opts)
      if (result === null) {
        setError({
          type: 'not_found',
          title: 'Erro ao regenerar',
          message: 'Não foi possível regenerar o resumo neste momento.',
          suggestion: 'Tente novamente em alguns segundos.',
          canRetry: true
        })
        return
      }
      setSummary(result)
      onSummaryGenerated?.(result)
      // BETA: incrementar contador após regeneração bem-sucedida
      if (betaMode) {
        incrementSummaryRegenCount(fileId)
        setRegenCount(getSummaryRegenCount(fileId))
      }
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
    } finally {
      setLoading(false)
    }
  }

  const toggleHighlight = (value: string) => {
    setCustomization(prev => {
      const current = prev.highlights ?? []
      const next = current.includes(value)
        ? current.filter(h => h !== value)
        : [...current, value]
      return { ...prev, highlights: next }
    })
  }

  // ===============================================
  // Painel de Customização
  // ===============================================
  const renderCustomizationPanel = () => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        bgcolor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon sx={{ color: '#1976d2', fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Personalizar Resumo
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => setShowCustomization(false)}
          startIcon={<CloseIcon />}
          sx={{ textTransform: 'none' }}
        >
          Fechar
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Foco do resumo */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
          📋 Foco da análise
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={customization.focus || 'geral'}
            onChange={(e) => setCustomization(prev => ({ ...prev, focus: e.target.value }))}
            sx={{ bgcolor: '#fff' }}
          >
            {FOCUS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{opt.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>{opt.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Nível de detalhe */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
          📏 Nível de detalhe
        </Typography>
        <RadioGroup
          row
          value={customization.detailLevel || 'moderado'}
          onChange={(e) => setCustomization(prev => ({ ...prev, detailLevel: e.target.value }))}
        >
          {DETAIL_OPTIONS.map(opt => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{opt.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>{opt.description}</Typography>
                </Box>
              }
              sx={{ mr: 3 }}
            />
          ))}
        </RadioGroup>
      </Box>

      {/* Perspectiva */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
          👁️ Perspectiva
        </Typography>
        <RadioGroup
          row
          value={customization.perspective || 'neutro'}
          onChange={(e) => setCustomization(prev => ({ ...prev, perspective: e.target.value }))}
        >
          {PERSPECTIVE_OPTIONS.map(opt => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{opt.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>{opt.description}</Typography>
                </Box>
              }
              sx={{ mr: 3 }}
            />
          ))}
        </RadioGroup>
      </Box>

      {/* Elementos a destacar */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
          🔍 Destacar elementos{' '}
          <Typography component="span" variant="caption" sx={{ color: '#888' }}>
            (selecione quantos quiser)
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
          {HIGHLIGHT_OPTIONS.map(opt => (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              variant={(customization.highlights ?? []).includes(opt.value) ? 'filled' : 'outlined'}
              color={(customization.highlights ?? []).includes(opt.value) ? 'primary' : 'default'}
              onClick={() => toggleHighlight(opt.value)}
              sx={{ cursor: 'pointer' }}
              title={opt.description}
            />
          ))}
        </Box>
      </Box>

      {/* Formato de saída */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
          📄 Formato do texto
        </Typography>
        <RadioGroup
          row
          value={customization.outputFormat || 'corrido'}
          onChange={(e) => setCustomization(prev => ({ ...prev, outputFormat: e.target.value }))}
        >
          {FORMAT_OPTIONS.map(opt => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{opt.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>{opt.description}</Typography>
                </Box>
              }
              sx={{ mr: 3 }}
            />
          ))}
        </RadioGroup>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          onClick={() => setCustomization({
            focus: 'geral', detailLevel: 'moderado', perspective: 'neutro',
            highlights: [], outputFormat: 'corrido',
          })}
          sx={{ textTransform: 'none' }}
        >
          Restaurar padrão
        </Button>
        <Tooltip title={betaMode && regenCount >= BETA_LIMITS.maxSummaryRegenerations ? 'Você já utilizou todas as regenerações disponíveis para este arquivo' : ''}>
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleRegenerate}
              disabled={betaMode && regenCount >= BETA_LIMITS.maxSummaryRegenerations}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Gerar com essas opções
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  )

  // Se a análise foi ignorada no upload, mostra card para executar sob demanda
  if (summaryStatus === 'SKIPPED') {
    return (
      <AnalysisSkippedCard
        title="Resumo com Inteligência Artificial"
        analysisType="summary"
        fileId={fileId}
        onAnalysisStarted={onAnalysisStarted}
      />
    )
  }

  // Se a análise está sendo processada (sob demanda), mostra card de processamento com animação
  if (summaryStatus === 'PROCESSING' && !summary && !loading) {
    return <AnalysisProcessingCard title="Resumo com Inteligência Artificial" />
  }

  // Estado inicial: resumo ainda não carregado
  if (!summary && !loading) {
    // Se a análise ainda está em processamento ou pendente
    const isProcessing = summaryStatus === 'PROCESSING' || summaryStatus === 'PENDING'
    const hasFailed = summaryStatus === 'FAILED'

    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: isProcessing ? '#fff3e0' : hasFailed ? '#fce4ec' : '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            {isProcessing ? (
              <CircularProgress size={40} sx={{ color: '#f57c00' }} />
            ) : (
              <AutoAwesomeIcon sx={{ fontSize: 40, color: hasFailed ? '#e53935' : '#1976d2' }} />
            )}
          </Box>

          {isProcessing ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Gerando resumo...
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 2, maxWidth: 400, mx: 'auto' }}>
                A análise está em andamento. O resumo será exibido automaticamente quando estiver pronto.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Resumo com Inteligência Artificial
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 3, maxWidth: 400, mx: 'auto' }}>
                {hasFailed
                  ? 'Houve um erro ao gerar o resumo durante a análise. Você pode tentar gerar novamente.'
                  : 'Visualize o resumo da transcrição com pontos-chave, ações pendentes e decisões identificadas.'}
              </Typography>
            </>
          )}

          {error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#e65100', fontWeight: 600, mb: 0.5 }}>
                {error.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#ef6c00' }}>
                {error.message}
              </Typography>
              {error.suggestion && (
                <Typography variant="caption" sx={{ color: '#f57c00', display: 'block', mt: 0.5 }}>
                  {error.suggestion}
                </Typography>
              )}
            </Box>
          )}

          {!isProcessing && (
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleGenerate}
              sx={{ borderRadius: 2 }}
            >
              {hasFailed ? 'Tentar gerar novamente' : 'Carregar Resumo'}
            </Button>
          )}

          {isProcessing && (
            <Typography variant="caption" sx={{ display: 'block', color: '#999', mt: 2 }}>
              ⏱️ Aguarde, isso pode levar alguns segundos...
            </Typography>
          )}
        </CardContent>
      </Card>
    )
  }

  // Estado de loading
  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Gerando resumo com IA...
            </Typography>
          </Box>

          <Stack spacing={2}>
            <Skeleton variant="text" width="100%" height={24} />
            <Skeleton variant="text" width="90%" height={24} />
            <Skeleton variant="text" width="95%" height={24} />
            <Skeleton variant="text" width="70%" height={24} />

            <Divider sx={{ my: 1 }} />

            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="rounded" width="100%" height={60} />

            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="rounded" width="100%" height={60} />
          </Stack>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              🤖 Analisando conteúdo e identificando informações relevantes...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  // Estado com resumo gerado
  return (
    <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
      <CardContent>
        {analysisOutdated && (
          <AnalysisOutdatedBanner
            analysisType="summary"
            onReanalyze={async () => {
              await runOnDemandAnalysis(fileId, 'summary')
              onAnalysisStarted?.()
            }}
            isReanalyzing={false}
          />
        )}
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between', 
          mb: 2,
          gap: { xs: 1.5, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: '#1976d2', fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Resumo com IA
            </Typography>
            <Chip
              label="Gerado automaticamente"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.7rem', display: { xs: 'none', sm: 'inline-flex' } }}
            />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 0.5, 
            alignItems: 'center',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' }
          }}>
            <Tooltip title="Copiar resumo">
              <IconButton size="small" onClick={handleCopyToClipboard} sx={{ color: '#666' }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Baixar como PDF">
              <IconButton size="small" onClick={handleDownloadPDF} sx={{ color: '#666' }}>
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />
            <Button
              size="small"
              startIcon={<TuneIcon />}
              onClick={() => setShowCustomization(!showCustomization)}
              sx={{ 
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 1.5 }
              }}
              color={showCustomization ? 'primary' : 'inherit'}
            >
              Personalizar
            </Button>
            <Tooltip title={betaMode && regenCount >= BETA_LIMITS.maxSummaryRegenerations ? 'Você já utilizou todas as regenerações disponíveis para este arquivo' : ''}>
              <span>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleRegenerate}
                  disabled={betaMode && regenCount >= BETA_LIMITS.maxSummaryRegenerations}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 1.5 }
                  }}
                >
                  {betaMode
                    ? `Regenerar (${Math.max(0, BETA_LIMITS.maxSummaryRegenerations - regenCount)} restante${BETA_LIMITS.maxSummaryRegenerations - regenCount !== 1 ? 's' : ''})`
                    : 'Regenerar'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Painel de Customização */}
        <Collapse in={showCustomization}>
          {renderCustomizationPanel()}
        </Collapse>

        {/* Banner informativo de personalização */}
        <Collapse in={showCustomizationHint && !showCustomization}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              mb: 2,
              bgcolor: '#e8f4fd',
              border: '1px solid #b3d9f2',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            <InfoOutlinedIcon sx={{ color: '#1976d2', fontSize: { xs: 20, sm: 22 }, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', lineHeight: 1.3, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                💡 Este resumo pode ser personalizado!
              </Typography>
              <Typography variant="caption" sx={{ color: '#1976d2', lineHeight: 1.4, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Clique em <b>Personalizar</b> para ajustar o foco (defesa, acusação, fatos), nível de detalhe, elementos a destacar e formato.
                Depois clique em <b>Regenerar</b> para gerar uma nova versão sob medida.
              </Typography>
            </Box>
            <Tooltip title="Fechar dica">
              <IconButton
                size="small"
                onClick={() => setShowCustomizationHint(false)}
                sx={{ color: '#90caf9', flexShrink: 0 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>
        </Collapse>

        {/* Bloco 1: Resumo principal com cabeçalho */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e0e0e0',
            borderLeft: '4px solid #1976d2',
            borderRadius: 2,
            mb: 3,
            overflow: 'hidden',
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: { xs: 1.5, sm: 2 },
            py: 0.75,
            bgcolor: '#f0f4ff',
            borderBottom: '1px solid #e3eaf8',
          }}>
            <AutoAwesomeIcon sx={{ color: '#1565c0', fontSize: 14 }} />
            <Typography sx={{ fontWeight: 700, color: '#1565c0', textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.68rem' }}>
              Visão Geral
            </Typography>
          </Box>
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              {summary?.overview}
            </Typography>
          </Box>
        </Paper>

        {/* Bloco 2: Detalhes da reunião (colapsável) */}
        {(summary?.mainTopic || summary?.context || (summary?.participants && summary.participants.length > 0)) && (
          <Box sx={{ mb: 3 }}>
            <Box
              onClick={() => setShowDetails(v => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: showDetails ? 1.5 : 0,
                cursor: 'pointer',
                userSelect: 'none',
                width: 'fit-content',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', fontSize: { xs: '0.82rem', sm: '0.875rem' } }}>
                Detalhes da reunião
              </Typography>
              <ExpandMoreIcon
                sx={{
                  color: '#888',
                  fontSize: 18,
                  transition: 'transform 0.2s',
                  transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Box>
            <Collapse in={showDetails}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                {summary?.mainTopic && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: '#f3e5f5',
                      borderRadius: 2,
                      border: '1px solid #e1bee7',
                      gridColumn: { xs: '1', md: summary?.context ? '1' : '1 / -1' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TopicIcon sx={{ color: '#7b1fa2', fontSize: { xs: 18, sm: 20 } }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#7b1fa2', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        Tópico Principal
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6 }}>
                      {summary.mainTopic}
                    </Typography>
                  </Paper>
                )}
                {summary?.context && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: '#e8f5e9',
                      borderRadius: 2,
                      border: '1px solid #c8e6c9',
                      gridColumn: { xs: '1', md: summary?.mainTopic ? '2' : '1 / -1' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <DescriptionIcon sx={{ color: '#388e3c', fontSize: { xs: 18, sm: 20 } }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#388e3c', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        Contexto
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6 }}>
                      {summary.context}
                    </Typography>
                  </Paper>
                )}
                {summary?.participants && summary.participants.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: '#e3f2fd',
                      borderRadius: 2,
                      border: '1px solid #bbdefb',
                      gridColumn: '1 / -1'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <PeopleIcon sx={{ color: '#1976d2', fontSize: { xs: 18, sm: 20 } }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        Participantes
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {summary.participants.map((participant, idx) => (
                        <Chip
                          key={idx}
                          label={getSpeakerName(participant)}
                          size="small"
                          sx={{
                            bgcolor: '#1976d2',
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                            height: { xs: 24, sm: 28 },
                            '&:hover': { bgcolor: '#1565c0' }
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Bloco 3: Itens identificados */}
        {((summary?.keyPoints && summary.keyPoints.length > 0) ||
          (summary?.actionItems && summary.actionItems.length > 0) ||
          (summary?.decisions && summary.decisions.length > 0)) && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Divider sx={{ flex: 1 }} />
              <Typography sx={{ fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                Itens Identificados
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            {/* Pontos-chave */}
            {summary?.keyPoints && summary.keyPoints.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LightbulbIcon sx={{ color: '#ed6c02', fontSize: { xs: 18, sm: 20 } }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                    Pontos-chave
                  </Typography>
                  <Chip label={summary.keyPoints.length} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700, ml: 0.5 }} />
                </Box>
                <List dense sx={{ bgcolor: '#fff8e1', border: '1px solid #ffe0b2', borderRadius: 2 }}>
                  {summary.keyPoints.map((point, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleOutlineIcon sx={{ color: '#ed6c02', fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Ações pendentes */}
            {summary?.actionItems && summary.actionItems.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TaskAltIcon sx={{ color: '#2e7d32', fontSize: { xs: 18, sm: 20 } }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                    Ações Pendentes
                  </Typography>
                  <Chip label={summary.actionItems.length} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: '#e8f5e9', color: '#1b5e20', fontWeight: 700, ml: 0.5 }} />
                </Box>
                <List dense sx={{ bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 2 }}>
                  {summary.actionItems.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Box sx={{ width: 18, height: 18, border: '2px solid #2e7d32', borderRadius: 1 }} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Decisões */}
            {summary?.decisions && summary.decisions.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GavelIcon sx={{ color: '#1976d2', fontSize: { xs: 18, sm: 20 } }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                    Decisões
                  </Typography>
                  <Chip label={summary.decisions.length} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: '#e3f2fd', color: '#0d47a1', fontWeight: 700, ml: 0.5 }} />
                </Box>
                <List dense sx={{ bgcolor: '#e3f2fd', border: '1px solid #bbdefb', borderRadius: 2 }}>
                  {summary.decisions.map((decision, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <GavelIcon sx={{ color: '#1976d2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText primary={decision} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}

        {/* Duração */}
        {summary?.duration && (
          <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 2, textAlign: 'right', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            Duração: {formatDuration(summary.duration)}
          </Typography>
        )}

        {/* Snackbar de feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  )
}
